"use client"

import * as React from "react"
import { DayPicker } from "react-day-picker"
import { ChevronLeft, ChevronRight } from "lucide-react"

import { cn } from "../../lib/utils"
import { buttonVariants } from "./button"

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: React.ComponentProps<typeof DayPicker>) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      navLayout={props.navLayout ?? "around"}
      className={cn("p-3", className)}
      classNames={{
        months: "flex flex-col gap-4 sm:flex-row sm:gap-6",
        month: "relative flex flex-col gap-4",
        month_caption: "flex h-10 items-center justify-center px-10",
        caption_label:
          "inline-flex items-center gap-1.5 whitespace-nowrap text-sm font-semibold text-foreground",
        dropdowns: "flex items-center gap-2",
        dropdown_root:
          "relative inline-flex h-9 items-center rounded-full border border-border bg-background px-3 text-sm font-medium text-foreground shadow-sm transition-colors focus-within:border-ring focus-within:ring-2 focus-within:ring-ring/20",
        dropdown:
          "absolute inset-0 z-10 cursor-pointer opacity-0 disabled:cursor-not-allowed",
        months_dropdown: "min-w-[7.5rem]",
        years_dropdown: "min-w-[5.5rem]",
        nav: "absolute inset-x-0 top-0 flex items-center justify-between",
        button_previous: cn(
          buttonVariants({ variant: "outline" }),
          "absolute left-0 top-0 h-9 w-9 rounded-full border-border bg-background/95 p-0 text-muted-foreground shadow-sm hover:bg-accent hover:text-foreground"
        ),
        button_next: cn(
          buttonVariants({ variant: "outline" }),
          "absolute right-0 top-0 h-9 w-9 rounded-full border-border bg-background/95 p-0 text-muted-foreground shadow-sm hover:bg-accent hover:text-foreground"
        ),
        chevron: "h-4 w-4 shrink-0 text-muted-foreground",
        month_grid: "w-full border-collapse",
        weekdays: "border-b border-border/60",
        weekday:
          "h-10 px-0 text-center align-middle text-xs font-medium tracking-wide text-muted-foreground",
        weeks: "mt-2",
        week: "",
        day: cn(
          "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-accent/70 [&:has([aria-selected].day-range-end)]:rounded-r-full [&:has([aria-selected].day-outside)]:bg-accent/30",
          props.mode === "range"
            ? "[&:has(>.day-range-end)]:rounded-r-full [&:has(>.day-range-start)]:rounded-l-full first:[&:has([aria-selected])]:rounded-l-full last:[&:has([aria-selected])]:rounded-r-full"
            : "[&:has([aria-selected])]:rounded-full"
        ),
        day_button: cn(
          buttonVariants({ variant: "ghost" }),
          "h-10 w-10 rounded-full p-0 text-sm font-medium text-foreground aria-selected:opacity-100 hover:bg-accent hover:text-accent-foreground"
        ),
        range_end: "day-range-end",
        range_start: "day-range-start",
        selected:
          "bg-primary text-primary-foreground hover:bg-primary/95 hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
        today:
          "bg-accent text-accent-foreground ring-1 ring-border ring-inset",
        outside:
          "day-outside text-muted-foreground/60 aria-selected:text-muted-foreground/70",
        disabled: "text-muted-foreground/45 opacity-100",
        range_middle:
          "aria-selected:bg-accent aria-selected:text-accent-foreground",
        hidden: "invisible",
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation }) => {
          const Icon = orientation === "left" ? ChevronLeft : ChevronRight
          return <Icon className="h-4 w-4" />
        },
      }}
      {...props}
    />
  )
}

export { Calendar }
